/**
 * 打开独立标签页的全屏编辑器。
 *
 * 顺序很讲究：`window.open` 必须在用户点击的同一个调用栈里同步执行，
 * 等完接口再开，浏览器多半当弹窗拦掉。所以先开一个空标签页，再去准备
 * 快照，准备好了把地址填进去；失败就把那个空页关掉。
 *
 * 新建模板 → 直接进这里，不再经过侧边栏那个窄编辑器（那是回头微调用的）。
 */
import { getBaseUserId } from '../api/bitable';
import type { FieldMeta } from '../api/types';
import type { ContractRenderData, ContractTemplate } from '../contract/types';
import { fetchContractData, prefetchAllLinks } from '../contract/dataFetcher';
import { getBaseIdSafe, getFieldMetaList, getPersonalBaseTokenSafe } from '../api/bitable';
import { linkTargets } from '../contract/linkedMeta';
import { flushPush, getTenant } from '../store/cloudSync';
import { putFsContext } from './fscontext';

export interface LaunchOpts {
  template: ContractTemplate;
  tableId: string;
  recordId: string | null;
  fieldMetas: FieldMeta[];
}

export type LaunchResult = 'opened' | 'blocked' | 'failed';

export async function launchFullscreen(opts: LaunchOpts): Promise<LaunchResult> {
  // 同步开：这一步之后的任何 await 都不影响它是否被拦
  const win = window.open('', '_blank');
  if (!win) return 'blocked';
  try {
    win.document.title = '正在打开编辑器…';
    win.document.body.innerHTML =
      '<div style="font:14px system-ui;color:#615847;padding:40px;text-align:center;">正在准备画布…</div>';
  } catch {
    /* 有的浏览器不让写空白页，无所谓 */
  }
  try {
    const t0 = performance.now();
    // 四件事互不依赖，一起跑：账号、当前记录的数据（含子表预取）、子表字段列表、
    // 模板推云端。以前串着做，用户盯着「正在准备画布」等好几秒。
    const dataP: Promise<ContractRenderData | null> =
      opts.tableId && opts.recordId
        ? fetchContractData(opts.tableId, opts.recordId, opts.template, opts.fieldMetas)
            .then(async (d) => {
              // 独立页里新配的明细表要能看到行：把所有关联字段的子表记录也带上
              const extra = await prefetchAllLinks(d.mainRecord as Record<string, unknown>, opts.fieldMetas).catch(() => ({}));
              return { ...d, linkedRecords: { ...extra, ...(d.linkedRecords ?? {}) } };
            })
            .catch(() => null)
        : Promise.resolve(null);
    // 每张关联表的字段列表：没有它，全屏里选了关联字段后属性栏会一直「正在读取」
    const linkedFieldMetas: Record<string, FieldMeta[]> = {};
    const metasP = Promise.all(
      linkTargets(opts.fieldMetas).map(async ({ tableId }) => {
        if (linkedFieldMetas[tableId]) return;
        try {
          linkedFieldMetas[tableId] = await getFieldMetaList(tableId);
        } catch {
          /* 读不到就让全屏里退回「未知字段」 */
        }
      }),
    );
    // 模板本身走云同步，先推一次，免得新标签页拉到的是旧版本。
    const pushP = flushPush();
    const [accountId, data, , personalBaseToken, baseId] = await Promise.all([
      getBaseUserId(), dataP, metasP, getPersonalBaseTokenSafe(), getBaseIdSafe(),
    ]);
    const ok = await putFsContext(accountId, {
      tableId: opts.tableId,
      recordId: opts.recordId,
      templateId: opts.template.id,
      fieldMetas: opts.fieldMetas,
      data,
      tableName: opts.template.name || '',
      tenantKey: getTenant() ?? undefined,
      linkedFieldMetas,
      personalBaseToken: personalBaseToken || undefined,
      baseId: baseId || undefined,
    });
    // 推不上去（KV 配额用完、断网）就别开页 —— 开了也是「找不到这个模板」。
    const pushed = await pushP;
    if (!ok || !pushed) {
      win.close();
      return 'failed';
    }
    console.info(`[launch] 准备画布 ${Math.round(performance.now() - t0)}ms`);
    win.location.href = `${window.location.origin}/?fs=1&account=${encodeURIComponent(accountId)}&tpl=${encodeURIComponent(opts.template.id)}`;
    return 'opened';
  } catch {
    try {
      win.close();
    } catch {
      /* ignore */
    }
    return 'failed';
  }
}
